import { AccountCurrencyCode, RoadVehicleType } from '@lune-climate/lune'
import { InferredRfqColumnAndValueMappings } from '@lune-fe/dapi-models'
import { toTitleCase } from '@lune-fe/lune-components-lib'
import { countriesList } from '@lune-fe/lune-ui-lib'
import Big from 'big.js'
import { CellValue, Row, Worksheet } from 'exceljs'
import levenshtein from 'fast-levenshtein'
import { isEqual, uniqWith } from 'lodash'
import { None, Option, Some } from 'ts-results-es'

import {
    extractLLMPropertyMapping,
    PER_LEG_LOCATION_PROPERTIES,
    repairLLMPropertyMapping,
} from 'hooks/useLLMColumnMapping'
import { assertType } from 'utils'
import { ExtendedParameterName } from 'views/CalculateEmissions/RFQ/components/ParameterMapping'
import { transportModeToGroupMapping } from 'views/CalculateEmissions/RFQ/components/TransportModeGroupSelect'
import { FileDetails } from 'views/CalculateEmissions/RFQ/RFQ'
import { getMappingDataSheet } from 'views/CalculateEmissions/RFQ/utils/operationUtils'
import {
    ColumnPropertyMapping,
    convertCellToString,
    CountryCode,
    countryCodeArray,
    CsvSheet,
    detectTableColumnSize,
    detectTableEndRow,
    detectTableStartColumn,
    DistanceUnit,
    distanceUnitsArray,
    extractRowValues,
    getMappedValue,
    IDynamicTransportModeMapping,
    loadUnitsArray,
    MappingDataSheet,
    ParameterName,
    PropertyMapping,
    RfqTemplateData,
    RoadFuelType,
    RouteMapping,
    RowNumber,
    StaticTransportModeMapping,
    Table,
    TemplateLegInfo,
    toCoordinate,
    toDate,
    toDateTime,
    toNonNegativeNumber,
    toNumber,
    toPositiveNumber,
    toVesselImo,
    TransportMode,
    transportModeArray,
    TransportModeGroup,
    TransportModeMappingData,
    ValueMappingInformation,
    ValueMappingSource,
} from 'views/CalculateEmissions/RFQ/utils/sharedLogic'
import {
    AutoFillEntryWithUpdate,
    ColumnMappingDetails,
    DisplayProperty,
    ExpectedRfqValueTypes,
    ExtraContextLabel,
    extraCountryAliases,
    inputAliases,
    isMappedProperty,
    KeysOfUnion,
    LegAutoFillWithUpdate,
    LegLevelMissingMappingData,
    MappedPropertyName,
    MissingMappingData,
    OptionalTransportModeMapping,
    PARAMETER_MAPPING_OUTPUT,
    RFQState,
    RFQTemplateState,
    TRANSPORT_MODES_PER_GROUP_PROPERTY,
    TransportModeProperty,
    TypedDapiRfqTemplate,
    unitAliases,
} from 'views/CalculateEmissions/RFQ/utils/types'

export function uniq<T extends string | number | boolean | symbol>(xs: T[]): T[] {
    return Array.from(new Set(xs))
}

export function uniqObj<T extends object>(xs: T[]): T[] {
    return uniqWith(xs, isEqual)
}

/**
 * Utility method that checks if a property exists in an object augmenting the type
 */
export function objectHasKey<T extends object, K extends PropertyKey>(
    obj: T | undefined,
    key: K,
): obj is T & Record<K, unknown> {
    return obj !== undefined && key in obj
}

export const labelsMap: Record<ExtendedParameterName, string> = {
    clientAccountName: 'Account Name',
    clientAccountCurrency: 'Account Currency',
    shippedAt: 'Shipped at',
    shipmentId: 'Shipment ID',
    shipmentIdAsIdempotencyKey: 'Only calculate each Shipment ID once, unless data changes',
    countryCode: 'Country Code',
    tradelane: 'Tradelane',
    transitCorridor: 'Transit Corridor',
    restrictedTransitCorridor: 'Restricted Transit Corridor',
    refrigeration: 'Refrigeration',
    aircraftType: 'Aircraft Type',
    vehicleType: 'Vehicle Type',
    fuel: 'Fuel',
    streetLine: 'Street Line',
    city: 'City',
    postcode: 'Postcode',
    airportCode: 'Airport Code',
    portCode: 'Port Code',
    lon: 'Longitude',
    lat: 'Latitude',
    distance: 'Distance',
    load: 'Load',
    loadSize: 'TEU',
    mode: 'Transport Mode',
    vesselName: 'Vessel Name',
    vesselImo: 'Vessel IMO',
    flightNumber: 'Flight Number',
    arrivalOn: 'Arrival On',
    departureOn: 'Departure On',
    transportMode: 'Transport Mode',
    'Container Ship_refrigeration': 'Refrigeration (Container Ship)',
    'Container Ship_tradelane': 'Tradelane (Container Ship)',
    'Container Ship_vesselName': 'Vessel Name (Container Ship)',
    'Container Ship_vesselImo': 'Vessel IMO (Container Ship)',
    'Container Ship_departureOn': 'Departure On (Container Ship)',
    'Container Ship_arrivalOn': 'Arrival On (Container Ship)',
    'Container Ship_transitCorridor': 'Transit Corridor (Container Ship)',
    'Container Ship_restrictedTransitCorridor': 'Restricted Transit Corridor (Container Ship)',
    'Cargo Plane_aircraftType': 'Aircraft Type (Cargo Plane)',
    'Cargo Plane_flightNumber': 'Flight Number (Cargo Plane)',
    'Cargo Plane_departureOn': 'Departure On (Cargo Plane)',
    'Passenger Plane_aircraftType': 'Aircraft Type (Passenger Plane)',
    'Passenger Plane_flightNumber': 'Flight Number (Passenger Plane)',
    'Passenger Plane_departureOn': 'Departure On (Passenger Plane)',
    Plane_aircraftType: 'Aircraft Type (Plane)',
    Plane_flightNumber: 'Flight Number (Plane)',
    Plane_departureOn: 'Departure On (Plane)',
    Truck_vehicleType: 'Vehicle Type (Van generic)',
    Truck_fuel: 'Fuel (Van generic)',
    'Inferred truck_fuel': 'Fuel (Inferred Truck)',
    'Truck rigid 7.5t_fuel': 'Fuel (Truck 7.5t)',
    'Truck rigid 12t_fuel': 'Fuel (Truck 12t)',
    'Truck rigid 20t_fuel': 'Fuel (Truck 20t)',
    'Truck rigid 26t_fuel': 'Fuel (Truck 26t)',
    'Truck rigid 32t_fuel': 'Fuel (Truck 32t)',
    'Truck articulated 34t_fuel': 'Fuel (Truck Articulated 34t)',
    'Truck articulated 40t_fuel': 'Fuel (Truck Articulated 40t)',
    'Truck articulated 44t_fuel': 'Fuel (Truck Articulated 44t)',
    'Truck articulated 60t_fuel': 'Fuel (Truck Articulated 60t)',
    'Truck articulated 72t_fuel': 'Fuel (Truck Articulated 72t)',
    Truck_refrigeration: 'Refrigeration (Van generic)',
    'Inferred truck_refrigeration': 'Refrigeration (Inferred Truck)',
    'Truck rigid 7.5t_refrigeration': 'Refrigeration (Truck 7.5t)',
    'Truck rigid 12t_refrigeration': 'Refrigeration (Truck 12t)',
    'Truck rigid 20t_refrigeration': 'Refrigeration (Truck 20t)',
    'Truck rigid 26t_refrigeration': 'Refrigeration (Truck 26t)',
    'Truck rigid 32t_refrigeration': 'Refrigeration (Truck 32t)',
    'Truck articulated 34t_refrigeration': 'Refrigeration (Truck Articulated 34t)',
    'Truck articulated 40t_refrigeration': 'Refrigeration (Truck Articulated 40t)',
    'Truck articulated 44t_refrigeration': 'Refrigeration (Truck Articulated 44t)',
    'Truck articulated 60t_refrigeration': 'Refrigeration (Truck Articulated 60t)',
    'Truck articulated 72t_refrigeration': 'Refrigeration (Truck Articulated 72t)',
    'Electric truck_refrigeration': 'Refrigeration (Electric truck)',
    'Electric van_refrigeration': 'Refrigeration (Electric van)',
}
type TransportModeMapping = TransportModeMappingData[keyof TransportModeMappingData]

export function parseBig(input: string): Big | undefined {
    try {
        return new Big(input)
    } catch {
        return undefined
    }
}

// eslint-disable-next-line complexity
function validateValueType(exampleValue: string, valueType: ExpectedRfqValueTypes): boolean {
    switch (valueType) {
        case 'locode':
            // TODO add warning system. Since we clean via LLM allow anything
            // return toLocode(exampleValue).isOk()
            return true
        case 'number':
            return toNumber(exampleValue).isOk()
        case 'positive_number':
            return toPositiveNumber(exampleValue).isOk()
        case 'non_negative_number':
            return toNonNegativeNumber(exampleValue).isOk()
        case 'airport_code':
            // TODO add warning system. Since we clean via LLM allow anything
            // return toAirportCode(exampleValue).isOk()
            return true
        case 'latitude':
            return toCoordinate('lat', exampleValue).isOk()
        case 'longitude':
            return toCoordinate('lon', exampleValue).isOk()
        case 'string':
            return true
        case 'vessel_imo':
            return toVesselImo(exampleValue).isOk()
        case 'date':
            return toDate(exampleValue).isOk()
        case 'datetime':
            return toDateTime(exampleValue).isOk()
        case 'unknown':
            /* Could be string or number or boolean, depending on mapping.
             * Leave it to backend to decide if it's valid data */
            return true
    }
}

/**
 * Iterates through all possible values for provided column and assert whether they conform to required data type (i.e.
 * they are of that type or can be converted to that type). Returns count of the number of unique values which do not
 * conform to the required type.
 * @param column - Array containing all possible unique values for a column
 * @param requiredDataType - The type requirement for data in provided column
 */
export function countInvalidDataTypeValues(
    column: string[],
    requiredDataType: ExpectedRfqValueTypes,
): number {
    return column.reduce(
        (invalidCount: number, data) =>
            !validateValueType(data, requiredDataType) ? invalidCount + 1 : invalidCount,
        0,
    )
}

export function extractDifferentColumnValues(
    sheet: MappingDataSheet,
    columnIndex: number,
    startingRowNumber: RowNumber,
    endRowNumber: RowNumber,
    allowedRowIndexes?: number[],
): string[] {
    const allValues = sheet
        .getColumn(columnIndex + 1)
        .values.slice(startingRowNumber.toOneBased(), endRowNumber.toOneBased())
        .filter((_e, i) => (allowedRowIndexes === undefined ? true : allowedRowIndexes.includes(i)))
        .map((e) => convertCellToString(e))
        .filter((e): e is string => e !== undefined)

    const uniqueValues: [string, string][] = []
    for (const val of allValues) {
        if (getMappedValue(uniqueValues, val).isNone()) {
            uniqueValues.push([val, val])
        }
    }

    return uniqueValues.map(([v1, _v2]) => v1)
}

export function getCountryCodeLabel(val: CountryCode): string {
    // The countries list should always have the country code but let's fallback just in case
    return (
        countriesList.find(([_n, _iso2, iso3]) => iso3.localeCompare(val) === 0)?.[0] ?? 'Unknown'
    )
}

export function isAccountCurrencyCode(value: unknown): value is AccountCurrencyCode {
    return (
        typeof value === 'string' &&
        new Set(Object.values(AccountCurrencyCode)).has(value as AccountCurrencyCode)
    )
}

export function generateInputWithAliases(input: string): string[] {
    return [input, ...(inputAliases[normalizeStringForInference(input)] ?? [])]
}

export function generateUnitWithAliases(unit: unknown): string[] {
    // For now all our unit types are just strings so converting here is safe
    return [String(unit), ...(unitAliases[String(unit)] ?? [])]
}

export function normalizeStringForInference(input: string): string {
    return input
        .replace(/[_\-.,:;+&]+/g, ' ') // replace with space since usually represent word boundary
        .replace(/[()[\]{}'"`“”’]/g, '') // remove unwanted special chars, doesn't make sense comparing
        .replace(/\s+/g, ' ') // collapse multiple whitespace characters into a single one space
        .trim()
        .toLowerCase()
}

// Hardcoded value below was found manually via some tests, might need adjustments
export const TYPO_FALLBACK_SCORE_THRESHOLD = 3
/**
 * Return the index for the `data` value which best matches the keywords provided. Best matches are determined by a
 * `score` value which is also returned.
 */
export function findBestStringMatchIndex(
    keywordsToSearch: string[],
    candidates: string[],
): { index: number; type: 'keyword' | 'typo'; score: number } {
    if (!keywordsToSearch.length || !candidates.length || !candidates.find((c) => !!c)) {
        throw new Error('Programming error, trying to find string matches with no data.')
    }
    let inferrence: { index: number; type: 'keyword' | 'typo'; score: number } | undefined
    const cleanKeywords = keywordsToSearch.map(normalizeStringForInference)
    const cleanCandidates = candidates.map(normalizeStringForInference)

    // Look for a substring match first which indicates a strong match
    cleanCandidates.forEach((candidate, index) => {
        if (!candidate) return

        for (const keyword of cleanKeywords) {
            if (candidate === keyword) {
                inferrence = { index, type: 'keyword', score: 0 }
            } else if (
                // Make sure we treat our keywords as words/sentences
                candidate.includes(` ${keyword} `) ||
                candidate.startsWith(`${keyword} `) ||
                candidate.endsWith(` ${keyword}`)
            ) {
                const matchStartIndex = candidate.indexOf(keyword)
                if (matchStartIndex !== -1) {
                    const matchEndIndex = matchStartIndex + keyword.length
                    const wordsBefore = candidate
                        .slice(0, matchStartIndex)
                        .split(' ')
                        .filter(Boolean).length // Used to remove empty strings
                    const wordsAfter = candidate
                        .slice(matchEndIndex)
                        .split(' ')
                        .filter(Boolean).length // Used to remove empty strings

                    // 1. Prefer early occurrences (alias appearing at the start = stronger match)
                    // 2. Prefer shorter names (fewer extra words = stronger match)
                    const score = wordsBefore * 2 + wordsAfter

                    if (!inferrence || score < inferrence.score) {
                        inferrence = { index, type: 'keyword', score }
                    }
                }
            }
        }
    })

    // Fallback to Levenshtein distance if substring match was not ideal
    if (!inferrence || inferrence.score >= TYPO_FALLBACK_SCORE_THRESHOLD) {
        cleanCandidates.forEach((candidate, index) => {
            if (!candidate) return
            for (const keyword of cleanKeywords) {
                const score = levenshtein.get(keyword, candidate)

                if (!inferrence || score < inferrence.score) {
                    inferrence = { score, type: 'typo', index }
                }
            }
        })
    }

    // Guaranteed to be populated since there are elements, at least one match had to occur
    return inferrence!
}

export function inferValueMapping<T>(
    inputValues: string[],
    outputValues: readonly T[],
    parameter: ParameterName,
): [string, T][] {
    const baseOutputValues: { label: string; value: T }[] = outputValues.map((v) => ({
        label: String(v),
        value: v,
    }))
    const additionalOutputAliases: { label: string; value: T }[] =
        parameter === 'countryCode'
            ? outputValues
                  // Parameter is `countryCode` so T is `string`. Casting types for simplicity
                  .map((v) => ({
                      label: getCountryCodeLabel(v as CountryCode),
                      value: v,
                  }))
                  .concat(extraCountryAliases.map((e) => ({ ...e, value: e.value as T })))
            : []
    const outputWithAliases = baseOutputValues.concat(additionalOutputAliases)
    return inputValues.map((input) => {
        const inputWithAliases = generateInputWithAliases(input)
        const bestMatch = findBestStringMatchIndex(
            inputWithAliases,
            outputWithAliases.map((v) => v.label),
        )
        return [input, outputWithAliases[bestMatch.index].value] as [string, T]
    })
}

export function inferColumnMapping<T>({
    columnName,
    columnValues,
    pickedColumnRowNumber,
    parameter,
    mappingType,
    outputValues,
}: {
    columnName: string
    columnValues: string[]
    pickedColumnRowNumber: RowNumber
    parameter: ParameterName
    mappingType: ValueMappingInformation<unknown>['source']
    outputValues: readonly T[]
}): PropertyMapping<T> {
    const baseMapping = {
        source: 'column' as const,
        columnIndex: pickedColumnRowNumber.toZeroBased(),
    }

    if (mappingType === 'direct') {
        return { ...baseMapping, valueMapping: { source: 'direct' } }
    } else if (mappingType === 'unit') {
        // For unit auto-complete the easiest for now is just look at the column name and units involved
        const bestUnitMatch = outputValues
            .map((e, index) => {
                const aliases = generateUnitWithAliases(e)
                const r = findBestStringMatchIndex(aliases, [columnName])
                return { score: r.score, type: r.type, index }
            })
            .reduce((a, b) => {
                // Prefer `keyword` matches if score is below threshold, otherwise compare scores.
                if (a.type === 'keyword' && a.score < TYPO_FALLBACK_SCORE_THRESHOLD) return a
                if (b.type === 'keyword' && b.score < TYPO_FALLBACK_SCORE_THRESHOLD) return b
                return a.score < b.score ? a : b
            })

        return {
            ...baseMapping,
            valueMapping: { source: 'unit', unit: outputValues[bestUnitMatch.index] },
        }
    } else {
        assertType<'mapping'>(mappingType)
        return {
            ...baseMapping,
            valueMapping: {
                source: 'mapping',
                mappings: inferValueMapping<T>(columnValues, outputValues, parameter),
            },
        }
    }
}

export function inferPropertyMapping<T>(
    state: RFQTemplateState | undefined,
    parameter: ParameterName,
    outputValues: readonly T[],
    extraContext?: { keywordExtra: ExtraContextLabel },
): PropertyMapping<T> | undefined {
    if (state === undefined || !state.llmResults) return undefined

    // Map parameter name to LLM property name
    let llmPropertyName: string = parameter
    const mappingType =
        parameter === 'load'
            ? ('unit' as const)
            : outputValues.length
              ? ('mapping' as const)
              : ('direct' as const)

    // Handle location properties that need source/destination prefixes
    if (
        extraContext?.keywordExtra &&
        [
            'countryCode',
            'city',
            'postcode',
            'streetLine',
            'lat',
            'lon',
            'airportCode',
            'portCode',
        ].includes(parameter)
    ) {
        const prefix = extraContext.keywordExtra === 'Origin' ? 'source' : 'destination'
        llmPropertyName = `${prefix}${toTitleCase(parameter)}`
    }

    // Use state.currentLeg for all properties except global 'load'
    const effectiveLegNumber = parameter === 'load' ? undefined : state.currentLeg

    const mapping = extractLLMPropertyMapping(
        state.llmResults,
        state.headers,
        llmPropertyName,
        effectiveLegNumber,
        outputValues,
        mappingType,
    )

    const columnValuesExtractor = (columnIndex: number) =>
        extractDifferentColumnValues(
            state.sheet,
            columnIndex,
            RowNumber.fromZeroBased(state.headerRow),
            RowNumber.fromZeroBased(state.endRow),
        )
    return mapping
        ? repairLLMPropertyMapping(
              mapping,
              mappingType,
              outputValues,
              parameter,
              columnValuesExtractor,
          )
        : undefined
}

export function getRoadVehicleLabel(val: RoadVehicleType): string {
    const mapping: Record<RoadVehicleType, string> = {
        truck_articulated_34t: 'Truck articulated 34t',
        truck_articulated_40t: 'Truck articulated 40t',
        truck_articulated_44t: 'Truck articulated 44t',
        truck_articulated_60t: 'Truck articulated 60t',
        truck_articulated_72t: 'Truck articulated 72t',
        truck_generic_van: 'Van generic ',
        truck_rigid_12t: 'Truck rigid 12t',
        truck_rigid_20t: 'Truck rigid 20t',
        truck_rigid_26t: 'Truck rigid 26t',
        truck_rigid_32t: 'Truck rigid 32t',
        truck_rigid_7_5t: 'Truck rigid 7.5t',
    }

    return mapping[val]
}

export function getRoadFuelLabel(val: RoadFuelType): string {
    const mapping: Record<RoadFuelType, string> = {
        '50_diesel_50_biodiesel': '50% Diesel / 50% Biodiesel',
        '80_diesel_20_biodiesel': '80% Diesel / 20% Biodiesel',
        '90_diesel_10_biodiesel': '90% Diesel / 10% Biodiesel',
        '93_diesel_7_biodiesel': '93% Diesel / 7% Biodiesel (B7)',
        '95_diesel_5_biodiesel': '95% Diesel / 5% Biodiesel (B5)',
        '98_diesel_2_biodiesel': '98% Diesel / 2% Biodiesel (B2)',
        '99_diesel_1_biodiesel': '99% Diesel / 1% Biodiesel (B1)',
        cng: 'CNG (Compressed Natural Gas)',
        diesel: 'Diesel',
        ethanol_from_corn: 'Ethanol (from Corn)',
        gasoline: 'Gasoline (Petrol)',
        hvo_from_tallow: 'HVO (Hydrotreated Vegetable Oil) from Tallow',
        lpg: 'LGP (Liquefied Petroleum Gas)',
        lng: 'LNG (Liquefied Natural Gas)',
        auto_detect: 'Auto Detect',
    }

    return mapping[val]
}

/**
 * Iterates through the example value for each header column, validating whether it can be converted to
 * the required data type. All column indexes which can't be converted to the required type are returned.
 * If all columns contain valid values, an empty array is returned.
 */
export function findInvalidColumnIndexes(
    headerData: string[],
    exampleRow: string[],
    requiredDataType: ExpectedRfqValueTypes,
): number[] {
    if (headerData.length !== exampleRow.length) {
        throw new Error(
            'Programming error, invalid example row provided. Must match header row dimensions',
        )
    }
    return headerData.reduce((invalidIndexes, _, i) => {
        if (!validateValueType(exampleRow[i], requiredDataType)) {
            invalidIndexes.push(i)
        }
        return invalidIndexes
    }, [] as number[])
}

function* iterateRows(sheet: Worksheet | CsvSheet): IterableIterator<[CellValue[], RowNumber]> {
    // Important: rowCount is also the number of the last row so the iteration needs
    // to be inclusive of that.
    for (let rowNumber = 1; rowNumber <= sheet.rowCount; rowNumber++) {
        yield [Object.values(sheet.getRow(rowNumber).values), RowNumber.fromOneBased(rowNumber)]
    }
}

/**
 * Returns if a row contains any value.
 */
function rowHasValues(
    row:
        | Row
        | {
              values: CellValue[]
          },
): boolean {
    if ('hasValues' in row) {
        return row.hasValues
    } else {
        return row.values.length !== 0
    }
}

/**
 * Attempts to detect the header row in a given `sheet`. The first plausible row
 * number is returned (in `Some`). `None` is returned if there are no candidates
 * detected.
 */
export function detectHeaderRow(sheet: Worksheet | CsvSheet): Option<RowNumber> {
    // Decided how many values do we need to even consider a given row a potential
    // header row. What should the value be here?
    const HEADER_COUNT_NEEDED = 3

    for (const [values, rowNumber] of iterateRows(sheet)) {
        // Do we expect other types (like rich text, dates or formulas) in header
        // rows usually? Leaning towards "no" for now, can be improved later.
        const headerCandidates = values.filter((v) => typeof v === 'string')
        if (headerCandidates.length < HEADER_COUNT_NEEDED) {
            continue
        }
        // Usually a header row means unique values, but we have seen some cases
        // (Nippon) where the headers have duplicates. This is most likely due to
        // several legs being present in the data and properties for different legs
        // sharing name. Require the number of unique values to be above a certain
        // threshold (value is an educated guess and might need tuning)
        const MANDATORY_UNIQUE_VALUES_PERCENTAGE = 80
        const uniqueCandidates = new Set(headerCandidates)
        const uniqueAboveThreshold =
            uniqueCandidates.size >
            headerCandidates.length * MANDATORY_UNIQUE_VALUES_PERCENTAGE * 0.01
        if (uniqueAboveThreshold) {
            const nextRowHasValues = rowHasValues(sheet.getRow(rowNumber.toOneBased() + 1))
            if (nextRowHasValues) {
                return Some(rowNumber)
            }
        }
    }
    return None
}

/**
 * Extract every property from a provided template which contains column mapping information
 */
function extractPropertyMappings(templateData: RfqTemplateData): ColumnMappingDetails[] {
    const results: ColumnMappingDetails[] = []
    if ('columnIndex' in templateData.load) {
        results.push({ propertyName: 'load', value: templateData.load })
    }

    function extractNestedPropertyMappings(
        parentProperty: TransportModeMapping | RouteMapping,
        baseInformation: {
            legIndex: number
            legProperty: keyof TemplateLegInfo
        },
        transportModeType?: keyof TransportModeMappingData,
    ) {
        for (const [propertyName, routePropertyMapping] of Object.entries(parentProperty)) {
            if (typeof routePropertyMapping !== 'object') {
                continue
            }
            if ('columnIndex' in routePropertyMapping) {
                results.push({
                    ...baseInformation,
                    ...(transportModeType ? { transportModeType } : {}),
                    propertyName,
                    value: routePropertyMapping as ColumnPropertyMapping<unknown>,
                })
            }
        }
    }

    templateData.legs.forEach((leg, i) => {
        for (const legPropertyName of Object.keys(leg) as (keyof typeof leg)[]) {
            const mapping = leg[legPropertyName]
            const baseInformation = {
                legIndex: i,
                legProperty: legPropertyName,
            }

            if ('mode' in mapping) {
                if ('columnIndex' in mapping.mode) {
                    results.push({
                        ...baseInformation,
                        propertyName: 'mode',
                        value: mapping.mode,
                    })
                }
                if (!mapping.data) {
                    return
                }

                for (const transportMode of Object.keys(
                    mapping.data,
                ) as (keyof typeof mapping.data)[]) {
                    const transportModeMapping = mapping.data[transportMode]

                    if ('value' in mapping.mode && transportMode !== mapping.mode.value) {
                        continue
                    }

                    extractNestedPropertyMappings(
                        transportModeMapping || {},
                        baseInformation,
                        transportMode,
                    )
                }
                continue
            }
            assertType<RouteMapping>(mapping)
            extractNestedPropertyMappings(mapping, baseInformation)
        }
    })

    return results
}

/**
 * Validates the provided template against the file parameter. Validation occurs in three dimensions:
 * 1. Validation of sheet name, table size and that columns in template exist in the data sheet
 * 2. Validation of data types in selected columns, e.g. ensuring load column contains numbers etc.
 * 3. Validation of template mapping values, e.g. ensuring all values in data sheet's `country_code` column already
 * appear in client template
 *
 * Failing (1) -> Template is unusable and `newTemplateRequired` flag is returned as `true`.
 * Failing (2) -> If all rows contain bad data, the template is also considered unusable, otherwise the template
 * is still considered usable but `badDataTypeProperties` is populated.
 * Failing (3) -> Template is still usable but `missingMappingProperties` is populated.
 */
// eslint-disable-next-line complexity
export async function validateSheetWithMapping(
    fileDetails: FileDetails,
    template: RfqTemplateData,
): Promise<
    | 'new_template_required'
    | {
          badDataTypeProperties: string[]
          missingMappingProperties: MissingMappingData[]
      }
> {
    const sheetName = template.sheetName
    const sheet = await getMappingDataSheet(
        fileDetails,
        sheetName,
        RowNumber.fromOneBased(template.headerRow),
    )

    if (!sheet) {
        return 'new_template_required'
    }

    const headerRowNumber = RowNumber.fromOneBased(template.headerRow)
    const headerStartColumn = detectTableStartColumn(sheet, headerRowNumber)
    const nrColumns =
        headerStartColumn.toZeroBased() +
        detectTableColumnSize(sheet, headerRowNumber, headerStartColumn)
    const headers = extractRowValues(sheet, headerRowNumber, nrColumns)
    if (!headers.length) {
        return 'new_template_required'
    }

    const mappings = extractPropertyMappings(template)
    const endRow = detectTableEndRow(
        sheet,
        headerRowNumber,
        nrColumns,
        headerStartColumn,
        sheetName,
    )
    if (endRow.toOneBased() === headerRowNumber.nextRow().toOneBased()) {
        return 'new_template_required'
    }
    const missingMappingProperties: MissingMappingData[] = []
    const badDataTypeProperties: string[] = []

    const legsPerTransportModeIndexes = template.legs.map((l) =>
        generatePerTransportModeRowIndexes(
            sheet,
            headerRowNumber.nextRow(),
            endRow,
            l.transportMode.mode,
        ),
    )
    const legsGroupProperties = template.legs.map((l) =>
        groupPropertiesToDisplayInMapping(l.transportMode.data, undefined),
    )
    for (const item of mappings) {
        const { value: mapping, propertyName, legProperty, legIndex, transportModeType } = item
        const type = mapping.valueMapping.source
        if (mapping.columnIndex > headers.length) {
            return 'new_template_required'
        }

        const isTransportModeProperty = transportModeType && legIndex !== undefined
        const groupProperty = isTransportModeProperty
            ? legsGroupProperties[legIndex].find(
                  (g) =>
                      g.parameter === propertyName &&
                      g.group === transportModeToGroupMapping[transportModeType],
              )
            : undefined
        let allowedRowIndexes: number[] | undefined
        if (isTransportModeProperty && groupProperty !== undefined) {
            const transportModes = getGroupParameterTransportModes(groupProperty)
            allowedRowIndexes = transportModes.flatMap(
                (m) => legsPerTransportModeIndexes[legIndex][m] ?? [],
            )
        } else if (isTransportModeProperty) {
            allowedRowIndexes = legsPerTransportModeIndexes[legIndex][transportModeType]
        }

        const columnValues = extractDifferentColumnValues(
            sheet,
            mapping.columnIndex,
            headerRowNumber.nextRow(),
            endRow,
            allowedRowIndexes,
        )

        if (type === 'direct' || type === 'unit') {
            badDataTypeProperties.push(propertyName)
        } else {
            assertType<'mapping'>(type)
            const mappings = mapping.valueMapping.mappings
            const difference = columnValues.filter((val) => getMappedValue(mappings, val).isNone())

            if (legIndex === undefined || !legProperty) {
                throw new Error(`Programming error: Missing leg data for ${propertyName}`)
            }

            if (!isMappedProperty(propertyName)) {
                throw new Error(`Programming error: Unexpected mapped property ${propertyName}`)
            }

            if (difference.length === 0) {
                continue
            }

            const baseMappingDetails = {
                legIndex,
                unmappedValues: difference,
            }

            if (propertyName === 'mode' || propertyName === 'countryCode') {
                missingMappingProperties.push({
                    type: 'legLevel',
                    name: propertyName,
                    legProperty,
                    ...baseMappingDetails,
                })
            } else {
                assertType<Exclude<MappedPropertyName, 'mode' | 'countryCode'>>(propertyName)
                if (!item.transportModeType || legProperty !== 'transportMode') {
                    throw new Error(
                        `Programming error: Expected transport mode type for property ${propertyName} on leg ${legIndex}`,
                    )
                }
                const baseProperty = {
                    ...baseMappingDetails,
                    legProperty,
                    name: propertyName,
                }
                if (groupProperty !== undefined) {
                    // Make sure group properties don't have duplicates
                    if (
                        missingMappingProperties.find(
                            (e) =>
                                e.type === 'group' &&
                                e.name === groupProperty.parameter &&
                                e.groupProperty.group === groupProperty.group,
                        ) === undefined
                    ) {
                        missingMappingProperties.push({
                            ...baseProperty,
                            type: 'group',
                            groupProperty,
                        })
                    }
                } else {
                    missingMappingProperties.push({
                        ...baseProperty,
                        type: 'transportMode',
                        transportModeType: item.transportModeType,
                    })
                }
            }
        }
    }
    return { badDataTypeProperties, missingMappingProperties }
}

/**
 * We have old version RFQ templates that might be used or editted. The FE needs to process
 * them. To keep things simple in the code though, we have choosen to only work with the new
 * version types, so in this method we migrate old version mappings to the newest version.
 */
export function migrateOldVersionMappings(mapping: TypedDapiRfqTemplate): TypedDapiRfqTemplate {
    const updatedLegs = []
    for (const leg of mapping.template.legs) {
        // We need to override types because we tell FE we only have the newest version and
        // it's not correct on this case. Old versions might have a static transport mode
        const typedTransportMode = leg.transportMode as
            | IDynamicTransportModeMapping
            | StaticTransportModeMapping

        let migratedTransportMode: IDynamicTransportModeMapping
        if ('type' in typedTransportMode) {
            // Get properties and add them to appropriate thing
            const { type, ...data } = typedTransportMode
            migratedTransportMode = {
                mode: { source: 'constant', value: type },
                data: {
                    'Container Ship': {},
                    'Cargo Plane': {},
                    Truck: {},
                    'Electric truck': {},
                    'Electric van': {},
                    'Electric train': {},
                    'Diesel train': {},
                    'Inland container vessel': {},
                },
            }
            migratedTransportMode.data![type] = data
        } else {
            migratedTransportMode = typedTransportMode
        }
        updatedLegs.push({
            ...leg,
            transportMode: migratedTransportMode,
        })
    }
    return { ...mapping, template: { ...mapping.template, legs: updatedLegs } }
}

/**
 * To avoid changing our template information we use an axios client without interceptors to
 * fetch the RFQ templates. This means that the expected type and the returned one will differ
 * since the API types are generated with the snake_case -> camelCase interceptor in mind.
 * In here we make sure the RFQ template adheres to the camelCase conversion in the properties
 * of the template, except the actual template data which we want the original one to match types.
 */
export function fixRfqMappingNoInterceptor(mapping: TypedDapiRfqTemplate): TypedDapiRfqTemplate {
    return {
        ...mapping,
        createdBy: (mapping as any).created_by,
        createdAt: (mapping as any).created_at,
        dataSheetId: (mapping as any).data_sheet_id,
        dataSheetAccountId: (mapping as any).data_sheet_account_id,
    }
}

/**
 * Helper method used whenever inference kicks in and auto-fills a field.
 */
export function updateBothAutoFill(state: AutoFillEntryWithUpdate | undefined, v: boolean) {
    state?.column.update(v)
    state?.mapping.update(v)
}

export function hasValueMapping(
    v: unknown,
): v is { valueMapping: { mappings: [string, unknown][] } } {
    function isNonNullObject(x: unknown): x is NonNullable<object> {
        return typeof x === 'object' && x !== null
    }

    if (
        isNonNullObject(v) &&
        'valueMapping' in v &&
        isNonNullObject(v.valueMapping) &&
        'mappings' in v.valueMapping &&
        Array.isArray(v.valueMapping.mappings)
    ) {
        return v.valueMapping.mappings.every(
            (m: unknown): m is [string, unknown] =>
                Array.isArray(m) && m.length === 2 && typeof m[0] === 'string',
        )
    }

    return false
}

/**
 * Extracts a value mapped property from a template using the details provided. Assumes the template legs are formatted
 * in the same structure as `TemplateLegInfo` type. If the property is a group property, all the mappings that are
 * referenced will be returned.
 *
 * TODO: Fix this assumption once we have template validation.
 */
export function findMappingsInTemplate(
    template: RfqTemplateData,
    propertyDetails: MissingMappingData,
): unknown[] {
    const leg = template.legs[propertyDetails.legIndex]
    if (propertyDetails.type === 'transportMode') {
        const perMode = leg.transportMode.data?.[propertyDetails.transportModeType]
        return [
            objectHasKey(perMode, propertyDetails.name) ? perMode[propertyDetails.name] : undefined,
        ]
    }
    if (propertyDetails.type === 'group') {
        // A group property must have all the transport modes in sync. Pick the first
        const transportModes = getGroupParameterTransportModes(propertyDetails.groupProperty)
        const modeData = leg.transportMode.data
        return transportModes.map((transportMode) => {
            const perMode = modeData?.[transportMode]
            return objectHasKey(perMode, propertyDetails.name)
                ? perMode[propertyDetails.name]
                : undefined
        })
    }
    assertType<LegLevelMissingMappingData>(propertyDetails)
    const segment = leg[propertyDetails.legProperty]
    return [objectHasKey(segment, propertyDetails.name) ? segment[propertyDetails.name] : undefined]
}

/**
 * Returns the value mapping for a given property in a specific leg. If no value mappings are found, None is returned.
 * If the property is a group property, only one of the transport mode mappings will be returned (they are in sync).
 *
 * @param propertyDetails - Object containing details necessary to find a specific value mapping, i.e. property name,
 * leg index, etc.
 */
export function getValueMappingFromTemplate(
    template: RfqTemplateData,
    propertyDetails: MissingMappingData,
): Option<[string, unknown][]> {
    const mapping = findMappingsInTemplate(template, propertyDetails)[0]
    return hasValueMapping(mapping) ? Some(mapping.valueMapping.mappings) : None
}

/**
 * Calculates and returns all table dimensions, ready to be written to our templates
 */
export function getTableDimensions(sheet: MappingDataSheet, headerRowNumber: RowNumber) {
    const headerStartColumn = detectTableStartColumn(sheet, headerRowNumber)
    const nrColumns =
        headerStartColumn.toZeroBased() +
        detectTableColumnSize(sheet, headerRowNumber, headerStartColumn)
    return {
        headerRowNumber,
        nrColumns,
        headerStartIndex: headerStartColumn.toZeroBased(),
        endRow: detectTableEndRow(sheet, headerRowNumber, nrColumns, headerStartColumn, sheet.name),
    }
}

// TODO Improve return type by changing PropertyMapping type.
/**
 * Generates a default mapping selection, prioritising LLM results.
 *
 * The returned mapping is guaranteed to be of the requested `mappingType`.
 */
export function createDefaultParameterMapping({
    state,
    columnIndex,
    parameter,
    outputValues,
    mappingType,
    columnValuesExtractor,
    legNumber,
    routeLabel,
}: {
    state: RFQTemplateState
    columnIndex: number
    parameter: ParameterName
    outputValues: readonly unknown[]
    mappingType: ValueMappingSource
    columnValuesExtractor: (columnIndex: number) => string[]
    legNumber?: number
    routeLabel?: 'Origin' | 'Destination'
}): PropertyMapping<unknown> {
    // Try LLM results first if available
    // Determine the property name for LLM mapping
    let llmPropertyName: string = parameter

    // Handle location properties that have source/destination variants
    const locationPropertyNames = PER_LEG_LOCATION_PROPERTIES.map((p) => p.name as string)
    if (locationPropertyNames.includes(parameter) && routeLabel) {
        const capitalizedProperty = toTitleCase(parameter)
        llmPropertyName =
            routeLabel === 'Origin'
                ? `source${capitalizedProperty}`
                : `destination${capitalizedProperty}`
    }

    const llmMapping = state.llmResults
        ? extractLLMPropertyMapping(
              state.llmResults,
              state.headers,
              llmPropertyName,
              legNumber,
              outputValues,
              mappingType,
          )
        : undefined

    // Use LLM mapping if it matches the selected column, otherwise fallback to manual inference
    return llmMapping?.source === 'column' && llmMapping.columnIndex === columnIndex
        ? repairLLMPropertyMapping(
              llmMapping,
              mappingType,
              outputValues,
              parameter,
              columnValuesExtractor,
          )
        : inferColumnMapping({
              columnName: state.headers[columnIndex],
              columnValues: columnValuesExtractor(columnIndex),
              pickedColumnRowNumber: RowNumber.fromZeroBased(columnIndex),
              parameter,
              mappingType,
              outputValues,
          })
}

/**
 * Given a transport mode mapping, provides an object that maps each transport mode to
 * a list of row indexes (0-based and counting from table start) for which this transport
 * mode is to be used. If the transport mode is not mapped by the user, the key will
 * not exist in the map.
 *
 * This is to be used in `select a column` transport mode selection, allowing us to be
 * able to know which rows in any column are to be used for a particular transport mode.
 *
 * As an example:
 * - User inputs 'select a column' in transport mode.
 * - Column 'C' is picked. Let's say it contains 3 distinct values: 'V1', 'V2', 'V3'.
 * - The actual cell values for the column in order are: 'V1', 'V3', 'V2', 'V1'
 * - Transport mode mapping is:
 *     - 'V1' -> 'Rail'
 *     - 'V2' -> 'Truck'
 *     - 'V3' -> 'Plane'
 * - The return of this function is a map where the keys are the mapped transport modes
 *   and the value the row indexes that are to be used for that transport mode.
 *
 * Final result: { 'Rail': [0, 3], 'Truck': [2], 'Plane': [1] }
 */
export function generatePerTransportModeRowIndexes(
    sheet: MappingDataSheet,
    startingRowNumber: RowNumber,
    endRowNumber: RowNumber,
    mapping: PropertyMapping<TransportMode>,
): { [K in TransportMode]?: number[] } {
    if (mapping.source !== 'column' || mapping.valueMapping.source !== 'mapping') {
        return {}
    }

    const mappings = mapping.valueMapping.mappings
    return sheet
        .getColumn(mapping.columnIndex + 1)
        .values.slice(startingRowNumber.toOneBased(), endRowNumber.toOneBased())
        .reduce(
            (acc, e, index) => {
                const cellValue = convertCellToString(e)
                if (cellValue === undefined) {
                    return acc
                }
                const transportMode = getMappedValue(mappings, cellValue)
                if (transportMode.isNone()) {
                    return acc
                }

                const updatedTransportModeIndexes = [...(acc[transportMode.value] ?? []), index]
                return {
                    ...acc,
                    [transportMode.value]: updatedTransportModeIndexes,
                }
            },
            {} as { [K in TransportMode]?: number[] },
        )
}

/**
 * Extract all the transport modes used in a group property. If the grouped property is
 * not currently supported an empty array is returned.
 */
export function getGroupParameterTransportModes(
    groupProperty: Extract<DisplayProperty, { type: 'group' }>,
): TransportMode[] {
    // Our logic should only be displaying supported group properties, but types don't
    // guarantee this so handle this case. It narrows the type for the next access
    if (!(groupProperty.parameter in TRANSPORT_MODES_PER_GROUP_PROPERTY[groupProperty.group])) {
        return []
    }
    return TRANSPORT_MODES_PER_GROUP_PROPERTY[groupProperty.group][
        groupProperty.parameter as keyof (typeof TRANSPORT_MODES_PER_GROUP_PROPERTY)[TransportModeGroup]
    ]
}

// TODO: Add tests. Currently hard to mock and type safety should make method quite safe
// (also tested things manually extensivelly).
/**
 * To prevent having to remove any existing user input in any transport mode property
 * mapping when the actual transport mode is modified, this function does a best effort
 * at trying to conciliate existing mappings due to transport mode mapping changes.
 *
 * The goal is use the existing mapping to try and populate the new mapping, allowing
 * 'select a column' transport mode selection to keep existing property mappings intact
 * as much as possible. This prevents us from having to completely start over with
 * property mappings if they exist in the previous mapping: if some mapping values are
 * no longer needed they are removed, if new mapping values are required we use the
 * default/inference value and mark the column as `autoFill`.
 *
 * NOTE: The above action of marking the column as `autoFill` is a side effect.
 */
// eslint-disable-next-line complexity
export function generateTransportModeMapping({
    state,
    previousTransportModeMapping,
    newTransportModeMapping,
    autoFill,
    options,
}: {
    state: RFQTemplateState
    previousTransportModeMapping: OptionalTransportModeMapping
    newTransportModeMapping: PropertyMapping<TransportMode>
    autoFill: LegAutoFillWithUpdate['transportMode']
    options: DisplayProperty[]
}): {
    mappingData: Partial<TransportModeMappingData>
    perTransportModeRowIndexes: ReturnType<typeof generatePerTransportModeRowIndexes>
} {
    const startingRowNumber = RowNumber.fromZeroBased(state.headerRow)
    const endRowNumber = RowNumber.fromZeroBased(state.endRow)
    const newPerTransportModeRowIndexes = generatePerTransportModeRowIndexes(
        state.sheet,
        startingRowNumber,
        endRowNumber,
        newTransportModeMapping,
    )

    // No data in previous mapping we can use
    if (
        !previousTransportModeMapping ||
        previousTransportModeMapping.mode === undefined ||
        previousTransportModeMapping.data === undefined
    ) {
        return { mappingData: {}, perTransportModeRowIndexes: newPerTransportModeRowIndexes }
    }

    const directTransportModes = Object.keys(newPerTransportModeRowIndexes) as TransportMode[]
    const groupProperties = options.filter(
        (o): o is Extract<DisplayProperty, { type: 'group' }> => o.type === 'group',
    )
    const groupTransportModes = groupProperties.flatMap(getGroupParameterTransportModes)

    const finalMappingProperties: Partial<TransportModeMappingData> = {}
    for (const transportMode of uniq(directTransportModes.concat(groupTransportModes))) {
        const transportModeGroup = transportModeToGroupMapping[transportMode]
        const previousPropertyMappings: TransportModeMappingData[TransportMode] =
            previousTransportModeMapping.data[transportMode] ?? {}
        const previousPropertyMappingsEntries = Object.entries(previousPropertyMappings) as [
            keyof typeof previousPropertyMappings,
            PropertyMapping<unknown> | undefined,
        ][]

        // Go through each property that the previous mapping had for the selected transport mode
        for (const [transportModeProperty, mapping] of previousPropertyMappingsEntries) {
            if (mapping === undefined) {
                continue
            }

            // If the transport mode does not directly exist in the new mapping and the property
            // is not being handled as a group property the property should not be migrated
            const isOnlyGroupProperty = !directTransportModes.includes(transportMode)
            const groupProperty = groupProperties.find(
                (g) => g.group === transportModeGroup && g.parameter === transportModeProperty,
            )
            if (isOnlyGroupProperty && groupProperty === undefined) {
                continue
            }
            // For constant, direct or unit mappings, we can just keep the previous mapping as is
            if (mapping.source === 'constant' || mapping.valueMapping.source !== 'mapping') {
                finalMappingProperties[transportMode] = {
                    ...finalMappingProperties[transportMode],
                    [transportModeProperty]: mapping,
                }
                continue
            }

            const allowedRowIndexes =
                groupProperty !== undefined
                    ? getGroupParameterTransportModes(groupProperty).flatMap(
                          (t) => newPerTransportModeRowIndexes[t] ?? [],
                      )
                    : newPerTransportModeRowIndexes[transportMode]
            // Get the default parameter mapping. Used as base to create the new one
            const defaultMapping = createDefaultParameterMapping({
                state,
                legNumber: state.currentLeg,
                columnIndex: mapping.columnIndex,
                parameter: transportModeProperty,
                outputValues: PARAMETER_MAPPING_OUTPUT[transportModeProperty],
                mappingType: 'mapping',
                columnValuesExtractor: (columnIndex: number) =>
                    extractDifferentColumnValues(
                        state.sheet,
                        columnIndex,
                        startingRowNumber,
                        endRowNumber,
                        allowedRowIndexes,
                    ),
            })

            // Types are a bit more broad than reality, we should be guaranteed to have a
            // column mapping with all the values present here. Narrow type and validate
            if (
                defaultMapping.source !== 'column' ||
                defaultMapping.valueMapping.source !== 'mapping'
            ) {
                throw new Error('default mapping not correctly generated')
            }
            // To generate the new mapping, go through the new possible values for the mapping
            // - If it exists in the old mapping use it as is.
            // - Otherwise use default/inferred value and mark the property as auto-filled.
            const newMappings: [string, unknown][] = []
            for (const [newMappingValue, mappedValue] of defaultMapping.valueMapping.mappings) {
                const previousMapping = getMappedValue(
                    mapping.valueMapping.mappings,
                    newMappingValue,
                )
                if (previousMapping.isSome()) {
                    newMappings.push([newMappingValue, previousMapping.value])
                } else {
                    newMappings.push([newMappingValue, mappedValue])
                    const autoFillProperty = autoFill[transportMode]
                    if (autoFillProperty && transportModeProperty in autoFillProperty) {
                        const entry: AutoFillEntryWithUpdate =
                            autoFillProperty[transportModeProperty]
                        entry.mapping.update(true)
                    }
                }
            }
            finalMappingProperties[transportMode] = {
                ...finalMappingProperties[transportMode],
                [transportModeProperty]: {
                    ...mapping,
                    valueMapping: {
                        ...mapping.valueMapping,
                        mappings: newMappings,
                    },
                },
            }
        }
    }

    return {
        mappingData: finalMappingProperties,
        perTransportModeRowIndexes: newPerTransportModeRowIndexes,
    }
}

export function inferAutoFillAndUpdate<T extends ParameterName>(
    state: RFQTemplateState | undefined,
    parameter: T,
    autoFill: LegAutoFillWithUpdate['transportMode' | 'origin'],
    extraContext?: { keywordExtra: ExtraContextLabel },
): PropertyMapping<(typeof PARAMETER_MAPPING_OUTPUT)[T][number]> | undefined {
    const inference = inferPropertyMapping<(typeof PARAMETER_MAPPING_OUTPUT)[T][number]>(
        state,
        parameter,
        PARAMETER_MAPPING_OUTPUT[parameter],
        extraContext,
    )
    for (const groupProperty of Object.keys(autoFill) as Array<keyof typeof autoFill>) {
        const groupProperties = autoFill[groupProperty]
        if (parameter in groupProperties) {
            updateBothAutoFill(
                groupProperties[parameter as keyof typeof groupProperties],
                !!inference,
            )
        }
    }
    return inference
}

/**
 * Determines the group properties that can be used for the given template data.
 */
function groupPropertiesToDisplayInMapping(
    transportModeMapping: Partial<TransportModeMappingData> | undefined,
    allowedTransportModes?: TransportMode[],
): Extract<DisplayProperty, { type: 'group' }>[] {
    const properties: Extract<DisplayProperty, { type: 'group' }>[] = []
    for (const [group, groupInfo] of Object.entries(TRANSPORT_MODES_PER_GROUP_PROPERTY)) {
        for (const [groupParameter, transportModesForParameter] of Object.entries(groupInfo)) {
            // null means no transport mode for property is allowed. `undefined` is a possible
            // value for the property mapping
            let groupPropertyValue = null as
                | null
                | undefined
                | 'mismatch'
                | PropertyMapping<unknown>
            for (const transportMode of transportModesForParameter as TransportMode[]) {
                if (allowedTransportModes && !allowedTransportModes.includes(transportMode)) {
                    continue
                }
                const transportModeMappingData = transportModeMapping?.[transportMode]
                const currentPropertyMapping =
                    transportModeMappingData && groupParameter in transportModeMappingData
                        ? transportModeMappingData[
                              // Our transportModeMapping and parametersByMode types aren't synced.
                              // We just checked for the property above so safe to assert type here
                              groupParameter as keyof typeof transportModeMappingData
                          ]
                        : undefined

                if (groupPropertyValue === null) {
                    groupPropertyValue = currentPropertyMapping
                } else if (!isEqual(groupPropertyValue, currentPropertyMapping)) {
                    groupPropertyValue = 'mismatch'
                    break
                }
                if (groupPropertyValue === 'mismatch') {
                    break
                }
            }

            if (groupPropertyValue !== 'mismatch' && groupPropertyValue !== null) {
                // Type assertions are safe since we iterate over the object (Object.entries erases types)
                properties.push({
                    type: 'group',
                    group: group as TransportModeGroup,
                    parameter: groupParameter as TransportModeProperty,
                })
            }
        }
    }
    return properties
}

/**
 * Determines which properties are to be displayed in the `Additional Properties` box. The
 * logic determines which properties can be used as group properties: a property becomes a
 * group property if all transport modes for that group have the same value for that property.
 * There is only two ways this is not the case:
 * - A direct transport mode is selected: example selecting `Truck` as the transport mode.
 * - An old template is being used and in the same group there is two or more transport modes
 *   that have the property with different values. For example, `fuel` in `Truck` is from
 *   column X but `fuel` in `Inferred Truck` is from column Y.
 *
 * To note on the above that not having a mapping is considered a value, example if `fuel`
 * in `Truck` exists but does not exist in `Inferred Truck` the property is not a group one.
 *
 * Note that new templates should always present/use group properties, while old templates
 * might present separate per transport mode properties. For the same transport group, a mix
 * of group properties and transport mode properties can co-exist: `fuel` in road might be a
 * group property while `refrigeration` is just a transport mode for `Electric van`.
 */
// eslint-disable-next-line complexity
export function propertiesToDisplayInMapping(
    transportModeMapping: OptionalTransportModeMapping,
    parametersByMode: Record<TransportMode, { [k in TransportModeProperty]?: unknown }>,
): DisplayProperty[] {
    const properties: DisplayProperty[] = []
    if (!transportModeMapping || !transportModeMapping.mode) {
        return properties
    }

    if (transportModeMapping.mode.source === 'constant') {
        const transportMode = transportModeMapping.mode.value
        for (const property of Object.keys(
            parametersByMode[transportMode],
        ) as TransportModeProperty[]) {
            properties.push({
                type: 'transportMode',
                transportMode,
                parameter: property,
            })
        }
        return properties
    }

    assertType<'column'>(transportModeMapping.mode.source)
    if (transportModeMapping.mode.valueMapping.source !== 'mapping') {
        // Our logic should never allow non mapping transport mode values
        throw new Error(`Only mapping expected here, got: ${JSON.stringify(transportModeMapping)}`)
    }

    const allModes = uniq(transportModeMapping.mode.valueMapping.mappings.map(([_from, to]) => to))

    // Detect if any group property can be used as group. This is the case for any new mappings, but
    // old mappings might have different property mappings for properties in the same group.
    properties.push(...groupPropertiesToDisplayInMapping(transportModeMapping.data, allModes))

    // Go through all the transport mode properties and add as needed: if not already present as group
    for (const transportMode of allModes) {
        const group = transportModeToGroupMapping[transportMode]
        for (const parameter of Object.keys(
            parametersByMode[transportMode],
        ) as TransportModeProperty[]) {
            if (
                properties.find(
                    (e) => e.type === 'group' && e.parameter === parameter && e.group === group,
                )
            ) {
                continue
            }

            properties.push({ type: 'transportMode', transportMode, parameter })
        }
    }

    return properties
}

/**
 * To avoid breaking backwards compatibility we have kept old properties. These should not be
 * shown on new mappings and are to be seen only if the mapping already contains a value. This
 * function cleans the full display properties and filters out these cases.
 */
export function cleanDisplayProperties(
    properties: DisplayProperty[],
    transportModeMapping: OptionalTransportModeMapping,
): DisplayProperty[] {
    const toFilterProperties: { mode: TransportMode; property: TransportModeProperty }[] = [
        { mode: 'Truck', property: 'vehicleType' },
    ]

    return properties.filter((p) => {
        if (p.type !== 'transportMode') {
            return true
        }

        if (
            toFilterProperties.find(
                (f) => p.transportMode === f.mode && p.parameter === f.property,
            ) === undefined
        ) {
            return true
        }
        const transportModeMappingData = transportModeMapping?.data?.[p.transportMode]
        const currentPropertyMapping = objectHasKey(transportModeMappingData, p.parameter)
            ? transportModeMappingData[p.parameter]
            : undefined

        return currentPropertyMapping !== undefined
    })
}

/**
 * Detects if a given table is a legacy logistic csv file (v1 or v2). This logic
 * is a best attempt since technically speaking there are no checks on the old
 * logistic files, anything is accepted. The schema is used to determine what
 * is expected on a valid legacy file.
 */
export function isLegacySheet(table: Table): boolean {
    // At least two rows are needed to correctly infer the file as legacy one
    if (table.length < 2) {
        return false
    }
    // Headers are always the first row for legacy sheets
    const headerRow = table[0]

    // We support EcoTransIT csv's so let us verify by using mandatory properties
    // All of these columns are required for EcoTransIT format
    const ecoTransItColumns = [
        'origin_location_type',
        'origin_location_code',
        'destination_location_type',
        'destination_location_code',
        'main_transport_mode',
        'freight_weight',
    ]
    if (ecoTransItColumns.every((f) => headerRow.includes(f))) {
        return true
    }

    // Version is the only mandatory field and we expect only two possible values
    const versionColumn = headerRow.findIndex((r) => r.trim() === 'version')
    const firstRowVersion = versionColumn === -1 ? undefined : table[1][versionColumn]?.trim()
    if (firstRowVersion === '1' || firstRowVersion === '2') {
        const hasShipment = ['mass_kg', 'containers'].some((f) => headerRow.includes(f))
        // Check source/destination exist for at least 1 leg.
        const hasSource =
            firstRowVersion === '2'
                ? headerRow.includes('source')
                : [
                      'pickup_country',
                      'pickup_city',
                      'pickup_postcode',
                      'pickup_street',
                      'pickup_coordinates',
                      'pickup_locode',
                      'pickup_airport_code',
                  ].some((f) => headerRow.includes(f))
        const hasDestination =
            firstRowVersion === '2'
                ? ['leg1_destination', 'leg1_distance_km'].some((f) => headerRow.includes(f))
                : [
                      'leg1_country',
                      'leg1_city',
                      'leg1_postcode',
                      'leg1_street',
                      'leg1_coordinates',
                      'leg1_locode',
                      'leg1_airport_code',
                      'leg1_distance_km',
                  ].some((field) => headerRow.includes(field))

        const hasMethod = headerRow.includes('leg1_method')
        return hasShipment && hasSource && hasDestination && hasMethod
    }

    return false
}

/**
 * Merges LLM inference results with users' final template to create a template which passes BE validation.
 */
export function mergeTemplateAndLlmState(
    userTemplate: RfqTemplateData,
    headers: string[],
    llmInference?: InferredRfqColumnAndValueMappings,
): RfqTemplateData {
    if (!llmInference) {
        return userTemplate
    }

    function extractLlmMPropertyMappingFromInput<T>(data: {
        propertyName: string
        mappingType: 'direct' | 'mapping' | 'unit'
        outputValues?: readonly T[]
        legNumber?: number
    }) {
        return extractLLMPropertyMapping<T>(
            // SAFETY: We return on an undefined `llmInference` above
            llmInference!,
            headers,
            data.propertyName,
            data.legNumber,
            data.outputValues ?? [],
            data.mappingType,
        )
    }

    const inferredLoad = extractLlmMPropertyMappingFromInput({
        propertyName: 'load',
        outputValues: loadUnitsArray,
        mappingType: 'unit',
    })

    // Helper to extract LLM inference results for the user's selected route type
    // eslint-disable-next-line complexity
    const buildInferredRoute = (
        userRoute: RouteMapping,
        legNumber: number,
        prefix: 'source' | 'destination',
    ): RouteMapping | undefined => {
        if (userRoute.type === 'Port code') {
            const mapping = extractLlmMPropertyMappingFromInput<string>({
                propertyName: `${prefix}PortCode`,
                legNumber,
                mappingType: 'direct',
            })
            return (
                mapping && {
                    type: 'Port code',
                    portCode: mapping,
                }
            )
        } else if (userRoute.type === 'Airport code') {
            const mapping = extractLlmMPropertyMappingFromInput<string>({
                propertyName: `${prefix}AirportCode`,
                legNumber,
                mappingType: 'direct',
            })
            return (
                mapping && {
                    type: 'Airport code',
                    airportCode: mapping,
                }
            )
        } else if (userRoute.type === 'Geo coordinates') {
            const lat = extractLlmMPropertyMappingFromInput<number>({
                propertyName: `${prefix}Lat`,
                legNumber,
                mappingType: 'direct',
            })
            const lon = extractLlmMPropertyMappingFromInput<number>({
                propertyName: `${prefix}Lon`,
                legNumber,
                mappingType: 'direct',
            })
            return (
                lat &&
                lon && {
                    type: 'Geo coordinates',
                    lat,
                    lon,
                }
            )
        } else if (userRoute.type === 'Address') {
            const countryCode = extractLlmMPropertyMappingFromInput<CountryCode>({
                propertyName: `${prefix}CountryCode`,
                legNumber,
                outputValues: countryCodeArray,
                mappingType: 'mapping',
            })
            const city = extractLlmMPropertyMappingFromInput<string>({
                propertyName: `${prefix}City`,
                legNumber,
                mappingType: 'direct',
            })
            const postcode = extractLlmMPropertyMappingFromInput<string>({
                propertyName: `${prefix}Postcode`,
                legNumber,
                mappingType: 'direct',
            })
            const streetLine = extractLlmMPropertyMappingFromInput<string>({
                propertyName: `${prefix}StreetLine`,
                legNumber,
                mappingType: 'direct',
            })

            if (countryCode || city || postcode || streetLine) {
                return {
                    type: 'Address',
                    countryCode,
                    postcode,
                    city,
                    streetLine,
                }
            }
            return undefined
        } else {
            assertType<'Distance'>(userRoute.type)
            const mapping = extractLlmMPropertyMappingFromInput<DistanceUnit>({
                propertyName: 'distance',
                legNumber,
                outputValues: distanceUnitsArray,
                mappingType: 'unit',
            })
            return (
                mapping && {
                    type: 'Distance',
                    distance: mapping,
                }
            )
        }
    }

    const inferredLegs: RfqTemplateData['inferredLegs'] = userTemplate.legs.map(
        (userLeg, legIndex) => {
            const legNumber = legIndex + 1
            const modeMapping =
                userLeg.transportMode.mode.source === 'column'
                    ? extractLlmMPropertyMappingFromInput<TransportMode>({
                          propertyName: 'transportMode',
                          legNumber,
                          outputValues: transportModeArray,
                          mappingType: 'mapping',
                      })
                    : undefined

            const llmInferredProperties: Partial<TransportModeMappingData> = {}
            for (const [mode, additionalPropertiesPerMode] of Object.entries(
                userLeg.transportMode.data || {},
            )) {
                const llmInferredTransportModeProperties: Partial<
                    Record<
                        KeysOfUnion<TransportModeMappingData[keyof TransportModeMappingData]>,
                        PropertyMapping<any>
                    >
                > = {}
                const mappedProperties = Object.entries(additionalPropertiesPerMode).filter(
                    ([_, mapping]) =>
                        mapping &&
                        typeof mapping === 'object' &&
                        'source' in mapping &&
                        mapping.source === 'column',
                )
                for (const [propertyName, propertyMapping] of mappedProperties as [
                    KeysOfUnion<TransportModeMappingData[keyof TransportModeMappingData]>,
                    Extract<PropertyMapping<unknown>, { source: 'column' }>,
                ][]) {
                    llmInferredTransportModeProperties[propertyName] =
                        propertyMapping.valueMapping.source === 'mapping'
                            ? extractLlmMPropertyMappingFromInput<unknown>({
                                  propertyName,
                                  legNumber,
                                  outputValues: propertyMapping.valueMapping.mappings.map(
                                      ([_, to]) => to,
                                  ),
                                  mappingType: 'mapping',
                              })
                            : extractLlmMPropertyMappingFromInput({
                                  propertyName,
                                  legNumber,
                                  mappingType: 'direct',
                              })
                }
                if (Object.keys(additionalPropertiesPerMode).length) {
                    llmInferredProperties[mode as keyof TransportModeMappingData] =
                        llmInferredTransportModeProperties
                }
            }

            return {
                transportMode:
                    modeMapping || Object.keys(llmInferredProperties).length
                        ? {
                              mode: modeMapping,
                              ...(Object.keys(llmInferredProperties).length
                                  ? { data: llmInferredProperties }
                                  : {}),
                          }
                        : undefined,
                origin: buildInferredRoute(userLeg.origin, legNumber, 'source'),
                destination: buildInferredRoute(userLeg.destination, legNumber, 'destination'),
            }
        },
    )

    return {
        ...userTemplate,
        inferredLoad,
        inferredLegs,
    }
}

export function showClientAccountsStep(state: RFQTemplateState | RFQState | undefined): boolean {
    return (
        state?.jobSettings?.targetClientAccount.type === 'per_row' ||
        (!!state?.rfqTemplate && !!state.clientAccountName)
    )
}
